import React, { useEffect, useState } from "react";
import { Container, Row, Col } from "react-bootstrap/esm/index";
import Helmet from "react-helmet";
import { helmetJsonLdProp } from "react-schemaorg";
import { pushOutboundClick } from "../../assets/datalayerFunctions";
import { Link } from "react-router-dom";

// Import Components
import ITPHeroHeader from "../../components/HeroHeader/ITPHeroHeader";
import Accordion from "../../components/shared-ui/Accordion/Accordion";
import ItpNavigation from "../../components/ITPNavigation/ITPNavigation";
import FooterITP from "../../components/shared-ui/Footer/Footer-ITP";
import BackToTopBtn from "../../components/shared-ui/BackToTopBtn/BackToTopBtn";
import ISIITPinline from "../../components/shared-ui/ISI/ISIITP-inline";
import ISIwrapper from "../../components/shared-ui/ISI/ISI-wrapper";
import ISIITP from "../../components/shared-ui/ISI/ISIITP";
import VideoLibrary from "../../components/VideoLibrary";
import PrimaryButton from "../../components/shared-ui/Buttons/PrimaryButton";
import LookingForSupport from "../../components/shared-ui/Cards/LookingForSupport";
import YoutubeEmbed from "../../components/shared-ui/Video/YoutubeEmbed";

import "../../css/PatientStories.scss";

function PatientStories() {
  const [chuckActive, setChuckActive] = useState(false);
  const toggleChuck = () => {
    setChuckActive(!chuckActive);
    scrollToTop();
  };
  const [tammyActive, setTammyActive] = useState(false);
  const toggleTammy = () => {
    setTammyActive(!tammyActive);
    scrollToTop();
  };

  const scrollToTop = () => {
    // @TODO: offset may be different on mobile.
    const scrollDiv = document.getElementById('scroll-anchor').offsetTop;
    window.scrollTo({
      top: scrollDiv - 140,
      behavior: 'smooth',
    });
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    let location = window.location.pathname;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      page: {
        path: `${location}`,
        title: "Patient Stories | DOPTELET® (avatrombopag)",
        environment: "dev",
        indication: "ITP",
        audience: "DTC",
      },
    });
  }, []);

  return (
    <div className="main_content_wrap">
      <Helmet>
        <title>
          Patient Stories | DOPTELET®
          (avatrombopag)
        </title>
        <meta
          name="description"
          content="Are you a patient or a caregiver of someone living with chronic ITP? Access our video resources, hear other patients’ experiences & learn more."
        />
        <link
          rel="canonical"
          href="https://doptelet.com/patient-stories/"
        />
      </Helmet>
      <ItpNavigation />
      <div className="inner-content-wrap">
        <ITPHeroHeader
          extraClass="large-hero inner-bg"
          markup={
            <>
              <h1>Patient Stories</h1>
              <p className="mb-0">
                Your story with chronic ITP is uniquely your own. But as your personal treatment journey continues to evolve, it can be helpful to hear from others who have been there before—like Tammy and Chuck. Both struggled with their ITP treatments before talking to their doctor about Doptelet. Individual patient results may vary.
              </p>
            </>
          }
          maxWidth="485"
          bgImage={["signs-symptoms-hero.jpg", "signs-symptoms-hero.jpg"]}
          bgImageMobile={[
            "signs-symptoms-hero-mobile.jpg",
            "signs-symptoms-hero-mobile@2x.jpg",
          ]}
          imageAlt="Doptelet characters playing games in carnival setting"
        />

        <div id="scroll-anchor"></div>

        {!chuckActive && !tammyActive && (
          <section className="cloud-blue-bg intro-section section-padding">
            <Container>
              <Row className="align-items-center">
                <Col lg={6} md={12}>
                  <h2 className="mb-3">Inspiring Treatment Possessions</h2>
                  <p className="mb-0 mb-lg-5">Watch Chuck and Tammy share personal stories and items that relate to their chronic ITP journeys.</p>
                </Col>
                <Col lg={6} md={12} className="mt-4 mt-lg-0">
                  <div className="video-section dopteller-diaries">
                    <div className="video-wrapper" style={{ maxWidth: 'inherit' }}>
                      <YoutubeEmbed
                        embedId="V8j9_w6w_Qo"
                        image={["chuck-and-tammy.jpg"]}
                        imageMobile={["chuck-and-tammy.jpg"]}
                        alt="Inspiring Treatment Possessions"
                      />
                    </div>
                    <Accordion
                      className="mt-3 mb-0"
                      title="Transcript"
                      markup={
                        <>
                          <p><strong>TAMMY:</strong> 	Hi, I’m Tammy and I’ve had chronic immune thrombocytopenia for over 15 years now.</p>

                          <p>

                            <strong>CHUCK:</strong>	And I’m Chuck, and I know it’s not a contest, but I’ve had it for 40 years. Tammy, you know what I brought today?
                          </p>
                          <p>
                            <strong>TAMMY:</strong>	What?
                          </p>
                          <p>
                            <strong>CHUCK:</strong>	Inspiring treatment possessions!
                          </p>

                          <p><strong>TAMMY:</strong>	I brought some, too. </p>

                          <p><strong>CHUCK:</strong>	Really? I want to know about your symptoms, your diagnosis, and your treatment and see how they’re the same.</p>

                          <p><strong>TAMMY:</strong>	I want to hear about yours, too. And also, then see how they’re different. </p>

                          <p><strong>CHUCK:</strong>	That would be cool. Yeah. Before I found a chronic ITP treatment that worked for me, I had to give up my e-bike.</p>

                          <p><strong>TAMMY:</strong>	Yeah, I understand, I used to ride a bike and a motorcycle too, but when my counts got really low, it was just not safe for me to be on the motorcycle anymore.</p>

                          <p><strong>CHUCK:</strong>	Before I found out my counts were so low, I’d do 40 or 50 miles a day. I got to the point where I was too worried, and I didn’t ride any bike at all. </p>

                          <p><strong>TAMMY:</strong>	My item isn’t as much fun as yours, but it’s headphones. When I was first diagnosed, I just couldn’t do a whole lot. You get so tired that it’s really hard to do much of anything else but sit around. So, I would binge-watch movies and TV shows and things like that. But if I got tired of that, I would just throw on my headphones and listen to an audiobook. </p>

                          <p><strong>CHUCK:</strong>	Sometimes when your counts are low, that’s about all you can do. </p>

                          <p><strong>TAMMY:</strong>	It’s really frustrating. Especially because I really like being active. I’m not one to just sit around and do nothing. And what was your diagnosis experience like? </p>

                          <p><strong>CHUCK:</strong>	It was a long time ago, back when my fanny pack was still popular. I’d had some bruising. I’d had some bleeding gums. Went in for a blood test for a minor surgical procedure, and they told me my platelets were low, and it turned out to be chronic ITP because it didn’t go away on its own. So how about you? </p>

                          <p><strong>TAMMY:</strong>	Well, this deck of cards represents my diagnosis. When I was a kid, we used to play cards a lot. So family is really important to me. And they were around really to support me throughout a lot of this.</p>

                          <p>When I had my son. My platelets stayed the same. And then when I had my daughter, my platelets dropped. So, they were like, well, what’s going on here? Many years later, I had to have a hysterectomy and had to have a blood transfusion due to some complications. My body then decided to just destroy all of my platelets. And then my family actually came into town. The support from them really helped me a lot. That’s what this deck of cards represents. So, that’s what led me into having chronic ITP. </p>

                          <p><strong>CHUCK:</strong> 	Holy cow. What happened next? </p>

                          <p><strong>TAMMY:</strong>	Well, that leads me into my next items. </p>

                          <p><strong>CHUCK:</strong>	What is all this? </p>

                          <p><strong>TAMMY:</strong>	Umm, that’s uh, just a bunch of stuff. Kind of represents all the treatment options I’ve been on. Steroids, immunosuppressants, IVIG—they just didn’t quite keep my counts up. </p>

                          <p><strong>CHUCK:</strong>	Yeah, I tried steroids years ago. And it just, it didn’t work for me. It didn’t work well enough. Man, I hated the way I felt.</p>

                          <p><strong>TAMMY:</strong>	So, since the steroids weren’t working for me, I tried some other options. There are a couple pills that I tried over the years that caused me to not be able to eat when I really necessarily wanted to, or would give food restrictions of some kind. Then I went on to an injection, and that one just stopped working for me too.</p>

                          <p><strong>CHUCK:</strong>	I had no idea there were that many options, you know? But I get the ups and downs. You know what I mean? My next item is a washer.</p>

                          <p><strong>TAMMY:</strong>	A tiny little washer?</p>

                          <p><strong>CHUCK:</strong>	A tiny little washer. Doctor found a spot about the size of this washer, maybe just a little smaller, on my lung. Needed to have surgery to get it checked out, removed. But my platelet count was too low. We tried the steroids again and the immunosuppressants, and we were absolutely convinced, my doctor and I, that we needed a long-term treatment. I should have been working together closer with my doctor for a long time. Back when I was wearing this. </p>

                          <p><strong>TAMMY:</strong>	Yep. Yeah, I got really lucky. I have a chronic ITP specialist in the area. We tried a lot early on, but if it wasn’t for continued research, going to the PDSA meetings, and talking to her, I wouldn’t have found Doptelet to treat my low blood platelet count when those other treatments just weren’t working well enough. Really is helpful to have that two-way communication with your doctor, talking about different options, different treatments and side effects that come along with the medications. So, when we talked about Doptelet, I found out that there were no food restrictions, which was really, really helpful. I could just eat what I wanted when I wanted to, just as long as I was taking it at a steady certain time of day. </p>

                          <p><strong>CHUCK:</strong>	That’d be really important to me too. I do like to eat. </p>

                          <p><strong>TAMMY:</strong>	So, this is my next item. Remember how I said I couldn’t ride the motorcycle anymore? But now with Doptelet, my platelet counts have been in a target range for a few years now. So, now I can go off roading. </p>

                          <p><strong>CHUCK:</strong>	That rules. </p>

                          <p><strong>TAMMY:</strong>	It’s really nice because even if I’m out driving with my husband and we get stuck somewhere. I still have my medication, and I can take it when I need to. </p>

                          <p><strong>CHUCK:</strong>	It really is about having good people in your corner, isn’t it? Let’s do the last one together. </p>

                          <p><strong>TAMMY:</strong>	That sounds like fun!</p>

                          <p><strong>CHUCK:</strong>	Of course, we both brought photos. </p>

                          <p><strong>TAMMY:</strong>	This is what it’s all about for me. Friends, family, coworkers. Ever since Doptelet was able to keep me in my goal range, it’s just been an incredible feeling. And it’s been great to be able to share it with all of them. </p>

                          <p><strong>CHUCK:</strong>	That’s really sweet, and I get it. These are the folks who taught me about what community means. My community now includes my doctor. And since my doctor introduced me to Doptelet, there have been many changes in my life. Now I’m able to do all kinds of things. In fact, I get together with my e-bike as often as I possibly can. </p>

                          <p><strong>TAMMY:</strong>	So, what does the future have in store for you? </p>

                          <p><strong>CHUCK:</strong>	I don’t know, but I am happy that my platelet counts within my goal range.</p>

                          <p><strong>TAMMY:</strong>	You’ve got to stay positive.</p>

                          <p><strong>CHUCK:</strong> 	And lifted. </p>

                          <p><strong>TAMMY:</strong>	Well, thank you for watching us go through our inspiring treatment possessions. </p>

                          <p><strong>CHUCK:</strong>	We hope you’ve enjoyed it as much as we have. </p>

                          <p><strong>VO:</strong>	Serious side effects, including blood clots, may occur. Tell your doctor about any swelling, pain or leg tenderness. Fast heartbeat, shortness of breath, chest or stomach pains or tenderness. Common side effects include headache, joint pain, fatigue, bleeding gums, bruising, petechiae, nosebleed, upper respiratory infection, and runny nose. Tell your doctor if you are breastfeeding, pregnant, or planning to be.</p>


                        </>
                      }
                    />
                  </div>
                </Col>
              </Row>
            </Container>
          </section>
        )}

        {/* CHUCK SECTION */}
        {chuckActive && (
          <section className="section-padding chuck-section">
            <Container>
              <Row>
                <Col lg={6} md={12}>
                  <button className="patient-stories-back pl-0" onClick={toggleChuck}><span>Back</span></button>
                  <h2 className="mb-3">Meet Chuck</h2>
                  <h3 className="mb-3">Finding Doptelet after a long journey</h3>
                  <p>If you ask Chuck what his favorite activity is, he will probably say taking long trips on his e-bike—but his chronic ITP meant that wasn’t always possible. For over 4 decades, Chuck has been navigating chronic ITP and tried many treatment options from steroids to immunosuppressants; but his platelet counts were still unpredictable. This meant activities like riding his e-bike were out of the picture. Because those previous treatments weren’t working well enough, Chuck talked with his doctor about starting Doptelet. Now, with his platelet counts stable at his target level, he doesn’t have to worry as much about the uncertainty of ITP.</p>
                  <div className="contained navy-bg d-block video-quote">
                    <p className="mb-0 text-white text-center">
                      <em>“Doptelet has helped lift me to this place where I feel like I have the energy to do more of the activities and things I love.”</em>
                      <span className="d-block mt-4">– Chuck</span>
                    </p>
                  </div>

                </Col>
                <Col lg={6} md={12} className="mt-4 mt-lg-0">
                  <div className="video-section dopteller-diaries">
                    <h3>
                      Inspiring Treatment Possessions: Chuck’s Story With Chronic ITP
                    </h3>
                    <div className="video-wrapper" style={{ maxWidth: 'inherit' }}>
                      <YoutubeEmbed
                        embedId="9faUORLlHGU"
                        image={["meet-chuck-video.jpg"]}
                        imageMobile={["meet-chuck-video.jpg"]}
                        alt="Inspiring Treatment Possessions: Chuck's Story with ITP"
                      />
                    </div>
                    <Accordion
                      className="mt-3 mb-0"
                      title="Transcript"
                      markup={
                        <>
                          <p><strong>CHUCK:</strong> Hi, I’m Chuck. I’m a transportation consultant from North Carolina. I’ve had chronic immune thrombocytopenia for more than 40 years. Today, I’m here to show you my inspiring treatment possessions.</p>

                          <p>Chronic ITP resulted in bruising and bleeding and made me a lot less active than I wanted to be. This radio kept me connected to all the sports and news and music that I needed.</p>

                          <p>Even though I couldn’t play sports all the time, I could certainly talk about them. That microphone represents all the times that I was able to do commentating and announcing. Maybe you’ve heard my voice on big time college wrestling.</p>

                          <p>When I finally got a diagnosis, it was definitely bittersweet. It was great knowing what was wrong and having some kind of an explanation for all that I’d lived with for so many years.</p>

                          <p>I’ve always liked all kinds of music. I brought this CD as a reminder—in times of uncertainty when you need an emotional boost, music was a great emotional boost for me.</p>

                          <p>Music is such a part of my life. Might be showtunes, might be bossa nova, might be classic rock, might be a little bit of all three. I’m just saying, any of you bands out there looking for a new frontman, call me.</p>

                          <p>To treat my chronic ITP the doctors tried steroids. We tried immunosuppressants. I didn’t like the way they worked for me, and they didn’t get my platelets into my target range.</p>

                          <p>My whole life I’ve been a voracious reader. Brought this book to represent the fact that when I’m going through symptoms, it was always an opportunity to find a story to, to occupy my mind. I was fortunate enough to find a great doctor who had new ideas about treatments. For me, this just reminds me that that was very important. You can never run out of books.</p>

                          <p>I dealt with treatments that weren’t quite effective enough. Before finally, the right doctor said I have an alternative. It’s called Doptelet. He told me that Doptelet was a treatment for adults with chronic ITP when other treatments haven’t worked well enough. Well, that sounded great to me.</p>

                          <p>One of my newer passions is riding an e-bike. I brought this bike helmet as a reminder of how happy I am to be back on the bike. When my platelet counts were too low, couldn’t ride as much as I wanted to, if at all. With my platelet count now in my target range, I get on that e-bike as often as I can.</p>

                          <p>Anyone who knows me knows that food is kind of important. I will describe where I went on a trip by what I had to eat and describing the restaurants.</p>

                          <p>This plate of food right now represents the fact that I’m thankful that I don’t have too many treatment restrictions with Doptelet. I can eat what I want. I can eat when I want. And because it’s a pill, I don’t have to see a doctor for any treatment injections. So not having food restrictions while taking Doptelet is an important thing for me.</p>

                          <p>I brought this joke book to tell you how much I like making people happy and making people laugh. If I can reach just one person and help them feel less alone, that’ll be more than worth it for me.</p>

                          <p>It’s been a long journey with chronic ITP. I’m so glad that I found the doctor I did find, but I wish I’d found that doctor sooner. I still have some limitations, but now that my platelets are within my target range I don’t have to monitor as often—which gives me the opportunity to do more of the things that I want to do.</p>

                          <p>I know I’ve enjoyed sharing my inspiring treatment possessions. I really hope it’s meant as much to you as it has to me.</p>

                          <p><strong>VO:</strong> Serious side effects, including blood clots, may occur. Tell your doctor about any swelling, pain or leg tenderness. Fast heartbeat, shortness of breath, chest or stomach pains or tenderness. Common side effects include headache, joint pain, fatigue, bleeding gums, bruising, petechiae, nosebleed, upper respiratory infection, and runny nose. Tell your doctor if you are breastfeeding, pregnant, or planning to be.</p>
                        </>
                      }
                    />
                  </div>

                </Col>
              </Row>
            </Container>
          </section>
        )}

        {/* TAMMY SECTION */}
        {tammyActive && (
          <section className="section-padding tammy-section">
            <Container>
              <Row>
                <Col lg={6} md={12}>
                  <button className="patient-stories-back pl-0" onClick={toggleTammy}><span>Back</span></button>
                  <h2 className="mb-3">Meet Tammy</h2>
                  <h3 className="mb-3">Looking forward to the future with Doptelet</h3>
                  <p>Tammy has had chronic ITP for 15 years, trying just about every treatment option available to her, like steroids and IVIG. But the medicines weren’t helping her stabilize her platelet counts. It was a roller coaster, and Tammy wanted to get off! Finally her doctor suggested Doptelet, and for the first time, Tammy heard that her platelet counts were steady. Now Tammy is excited about the activities that she loves, like hiking, playing golf, and having fun with friends.</p>
                  <div className="contained navy-bg d-block video-quote">
                    <p className="mb-0 text-white text-center">
                      <em>“Over these last few years, I never really thought a lot about the future as much because I was always going day to day or week to week. Now, with being on Doptelet, I can actually think more ahead. I can just be me.”</em>
                      <span className="d-block mt-4">– Tammy</span>
                    </p>
                  </div>

                </Col>
                <Col lg={6} md={12} className="mt-4 mt-lg-0">
                  <h3>
                    Inspiring Treatment Possessions: Tammy’s Story With Chronic ITP
                  </h3>
                  <div className="video-section dopteller-diaries">
                    <div className="video-wrapper" style={{ maxWidth: 'inherit' }}>
                      <YoutubeEmbed
                        embedId="MKPB1GUpY38"
                        image={["meet-tammy-video.jpg"]}
                        imageMobile={["meet-tammy-video.jpg"]}
                        alt="Inspiring Treatment Possessions: Tammy's Story with ITP"
                      />
                    </div>
                    <Accordion
                      className="mt-3 mb-0"
                      title="Transcript"
                      markup={
                        <>
                          <p><strong>TAMMY:</strong> I'm Tammy and I'm a teacher in Washington state. I've had chronic immune thrombocytopenia for over 15 years now. Today I am showing you my inspiring treatment possessions.</p>

                          <p>I'm usually a pretty active person, but when I was first diagnosed, I was pretty worn out so my remote became my friend. I couldn't even sit and read a book sometimes. It was just too tiring. So, grab the kids, grab the remote, put on a TV show or movie and off we go.</p>

                          <p>It can be difficult in the beginning when being diagnosed with chronic ITP, most people don't know how they got it, so it can be a little scary that way.</p>

                          <p>After being diagnosed with ITP I couldn't do my normal activities anymore so I got out my crocheting again, which I had forgotten how much I love to do. It was nice and calming. It made me feel like I was active again, even though I really wasn't moving around much.</p>

                          <p>I brought this mug with the hot chocolate in it, because it really reminds me of a time when my plans had to be tentative. I had a lot of low energy due to my ITP. I couldn't always follow through with activities that I had planned. My friends all knew that, but sometimes I could have them over and we could sit around and chat and have some hot chocolate together.</p>

                          <p>Over the years I had a lot of different treatments. IVIG, steroids, immunosuppressants, and some others. Some of those treatments did help, but just not well enough. There was still a lot of uncertainty. My platelet counts were all over the place and I had some bad reactions as well.</p>

                          <p>What's next? Well, it’s Doptelet. After more than a decade of struggling with my chronic ITP, I finally found the right treatment for me. For those that don't know, Doptelet helps treat low blood platelets in adults with chronic ITP when other treatments haven't worked well enough.</p>

                          <p>I brought this golf ball along because, like I said, I really like to be active. Can't play softball anymore. So, I traded it in for a golf ball. This is just one of the many ways I'm able to stay active on Doptelet. Because my platelets are in their target range now, I can be out all day and not have to worry about scheduling around my doctor's appointments or treatment injections.</p>

                          <p>Another possession I have are my hiking boots. I really like being active and being out in nature, and when I'm out hiking, I get hungry. I usually take a lot of snacks with me. Now that I'm on Doptelet, there are no food restrictions that I have to worry about.</p>

                          <p>This next item is my passport. It's great that I can take my Doptelet wherever I am. That means I can go away on a spontaneous weekend or even a longer trip abroad. Now that my platelet counts are within my target range, I don't need to monitor them as much as I used to.</p>

                          <p>My treatment journey with ITP has had a lot of ups and downs. I think I tried just about everything available to me. With all that uncertainty, it was really hard to live my life the way I really wanted to. I do still have some limitations, but my platelet counts are more predictable, which gives me the opportunity to do the things I love again. That is why I'm thankful I found Doptelet.</p>

                          <p>It’s just so special looking back on everything. These are all my inspiring treatment possessions. I hope you enjoyed it as much as I have.</p>

                          <p><strong>VO:</strong> Serious side effects, including blood clots, may occur. Tell your doctor about any swelling, pain or leg tenderness. Fast heartbeat, shortness of breath, chest or stomach pains or tenderness. Common side effects include headache, joint pain, fatigue, bleeding gums, bruising, petechiae, nosebleed, upper respiratory infection, and runny nose. Tell your doctor if you are breastfeeding, pregnant, or planning to be.</p>

                        </>
                      }
                    />
                  </div>
                </Col>
              </Row>
            </Container>
          </section>
        )}

        {!chuckActive && !tammyActive && (
          <section className="section-padding pb-0 story-selector">
            <Container>
              <h2 className="mb-5">Real Patient Stories</h2>
              <Row className="align-items-center">
                <Col md={12} lg={6} className="mb-4 mb-lg-0">
                  <div className="meet-person">
                    <img src="/images/chuck.jpg" alt="Doptelet Patient Chuck" className="w-100 img-fluid" />
                    <h3 className="mt-3">Meet Chuck</h3>
                    <p className="mb-4">Chuck’s been navigating his chronic ITP for over 40 years. See his treatment journey and experience with Doptelet.</p>
                    <PrimaryButton
                      title="LEARN MORE"
                      href="#"
                      onClick={toggleChuck}
                      addClass="btn-arrow btn-primary w-100"
                    />
                  </div>
                </Col>
                <Col md={12} lg={6}>
                  <div className="meet-person">
                    <img src="/images/tammy.jpg" alt="Doptelet Patient Tammy" className="w-100 img-fluid" />
                    <h3 className="mt-3">Meet Tammy</h3>
                    <p className="mb-4">Tammy’s history with chronic ITP was a roller coaster before Doptelet. Read about her treatment journey.</p>
                    <PrimaryButton
                      title="LEARN MORE"
                      href="#"
                      onClick={toggleTammy}
                      addClass="btn-arrow btn-primary w-100"
                    />
                  </div>
                </Col>
              </Row>
            </Container>
          </section>
        )}

        <section className={chuckActive || tammyActive ? 'section-padding cloud-blue-bg' : 'section-padding pb-0'}>
          <Container>
            <Row className="align-items-center">
              <Col md={{ span: 12 }}>
                <div>
                  <h2>Have your own Doptelet story?</h2>
                  <p>We’re searching for people to inform and inspire others living with chronic ITP by sharing their experiences on Doptelet.</p>
                  <Link
                    to="/support-and-resources/share-your-story/"
                    className="btn btn-arrow btn-primary btn-white">
                    <span>SHARE YOUR STORY</span>
                  </Link>
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Only show video library when chuck or tammy are active */}
        {(chuckActive || tammyActive) && (
          <section className="section-padding pb-0">
            <Container>
              <Row>
                <Col>
                  <h2>Related Videos</h2>
                  <VideoLibrary activePatient={chuckActive ? 'chuck' : 'tammy'} />
                </Col>
              </Row>
            </Container>
          </section>
        )}

        <LookingForSupport addClass="section-padding" />

        <ISIITPinline />
      </div>
      <FooterITP />
      <ISIwrapper>
        <ISIITP />
      </ISIwrapper>
      <BackToTopBtn />
    </div>
  );
}

export default PatientStories;
